import apiServices from "../apiServices";
import { validateOrThrowApiResponse } from "../response-validator";

type CorrectAnswerPayloadItem = {
    id?: number | string;
    no: number;
    expected_time?: number;
    correct_answer: string;
    wrong_answer: string;
    hint?: string | null;
    sound_correct_answer?: string | null;
    image_correct_answer?: string | null;
    sound_wrong_answer?: string | null;
    image_wrong_answer?: string | null;
    sound_hint?: string | null;
    image_hint?: string | null;
};

type CorrectAnswerSkillPayload = {
    skill_listening_speaking?: boolean;
    skill_lang_nature?: boolean;
    skill_reading?: boolean;
    skill_writing?: boolean;
};

type CorrectAnswerAudioPayload = {
    audio_question?: boolean;
	audio_choice?: boolean;
	audio_assistant_voice?: boolean;
	audio_background?: boolean;
};

type CreateCorrectAnswerPayload = CorrectAnswerSkillPayload & CorrectAnswerAudioPayload & {
    gameId: number | string;
    correct_answers: CorrectAnswerPayloadItem[];
};

type GetCorrectAnswerByIdPayload = {
    gameId: number | string;
};

type UpdateCorrectAnswerPayload = CorrectAnswerSkillPayload & CorrectAnswerAudioPayload & {
    correctAnswerId: number | string;
    correct_answer?: string;
    wrong_answer?: string;
    no?: number;
    expected_time?: number;
    hint?: string | null;
    sound_correct_answer?: string | null;
    image_correct_answer?: string | null;
    sound_wrong_answer?: string | null;
    image_wrong_answer?: string | null;
    sound_hint?: string | null;
    image_hint?: string | null;
};

type DeleteCorrectAnswerPayload = {
    correctAnswerId: number | string;
};

const CORRECT_ANSWER_MEDIA_FIELDS = [
    "sound_correct_answer",
    "sound_wrong_answer",
    "image_correct_answer",
    "image_wrong_answer",
    "hint",
    "sound_hint",
    "image_hint",
] as const;

type CorrectAnswerMediaField = (typeof CORRECT_ANSWER_MEDIA_FIELDS)[number];

type HardDeleteMediaFieldPayload = {
    correctAnswerId: number | string;
    field: CorrectAnswerMediaField;
};


const correctAnswerAPI = {

    createCorrectAnswer(payload: CreateCorrectAnswerPayload) {
        const requestBody: {
            correctAnswers: CorrectAnswerPayloadItem[];
            skill_listening_speaking?: boolean;
            skill_lang_nature?: boolean;
            skill_reading?: boolean;
            skill_writing?: boolean;
            audio_question?: boolean;
            audio_choice?: boolean;
            audio_assistant_voice?: boolean;
            audio_background?: boolean;
        } = {
            correctAnswers: payload.correct_answers,
        };

        if (payload.skill_listening_speaking !== undefined) {
            requestBody.skill_listening_speaking = payload.skill_listening_speaking;
        }
        if (payload.skill_lang_nature !== undefined) {
            requestBody.skill_lang_nature = payload.skill_lang_nature;
        }
        if (payload.skill_reading !== undefined) {
            requestBody.skill_reading = payload.skill_reading;
        }
        if (payload.skill_writing !== undefined) {
            requestBody.skill_writing = payload.skill_writing;
        }
        if (payload.audio_question !== undefined) {
            requestBody.audio_question = payload.audio_question;
        }
        if (payload.audio_choice !== undefined) {
            requestBody.audio_choice = payload.audio_choice;
        }
        if (payload.audio_assistant_voice !== undefined) {
            requestBody.audio_assistant_voice = payload.audio_assistant_voice;
        }
        if (payload.audio_background !== undefined) {
            requestBody.audio_background = payload.audio_background;
        }

        return apiServices
            .post(`/api/game/${payload.gameId}/correct-answers`, requestBody, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error createCorrectAnswer:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    getCorrectAnswerById(payload: GetCorrectAnswerByIdPayload) {
        return apiServices
            .get(`/api/game/${payload.gameId}/correct-answers`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error getCorrectAnswerById:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    getAllCorrectAnswers() {
        return apiServices
            .get(`/api/correct-answers`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error getAllCorrectAnswers:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    updateCorrectAnswer(payload: UpdateCorrectAnswerPayload) {
        const requestBody: {
            correct_answer?: string;
            wrong_answer?: string;
            no?: number;
            expected_time?: number;
            hint?: string | null;
            sound_correct_answer?: string | null;
            image_correct_answer?: string | null;
            sound_wrong_answer?: string | null;
            image_wrong_answer?: string | null;
            sound_hint?: string | null;
            image_hint?: string | null;
            skill_listening_speaking?: boolean;
            skill_lang_nature?: boolean;
            skill_reading?: boolean;
            skill_writing?: boolean;
            audio_question?: boolean;
            audio_choice?: boolean;
            audio_assistant_voice?: boolean;
            audio_background?: boolean;
        } = {};

        if (payload.correct_answer !== undefined) requestBody.correct_answer = payload.correct_answer;
        if (payload.wrong_answer !== undefined) requestBody.wrong_answer = payload.wrong_answer;
        if (payload.no !== undefined) requestBody.no = payload.no;
        if (payload.expected_time !== undefined) requestBody.expected_time = payload.expected_time;
        if (payload.hint !== undefined) requestBody.hint = payload.hint;
        if (payload.sound_correct_answer !== undefined) requestBody.sound_correct_answer = payload.sound_correct_answer;
        if (payload.image_correct_answer !== undefined) requestBody.image_correct_answer = payload.image_correct_answer;
        if (payload.sound_wrong_answer !== undefined) requestBody.sound_wrong_answer = payload.sound_wrong_answer;
        if (payload.image_wrong_answer !== undefined) requestBody.image_wrong_answer = payload.image_wrong_answer;
        if (payload.sound_hint !== undefined) requestBody.sound_hint = payload.sound_hint;
        if (payload.image_hint !== undefined) requestBody.image_hint = payload.image_hint;
        if (payload.skill_listening_speaking !== undefined) {
            requestBody.skill_listening_speaking = payload.skill_listening_speaking;
        }
        if (payload.skill_lang_nature !== undefined) {
            requestBody.skill_lang_nature = payload.skill_lang_nature;
        }
        if (payload.skill_reading !== undefined) {
            requestBody.skill_reading = payload.skill_reading;
        }
        if (payload.skill_writing !== undefined) {
            requestBody.skill_writing = payload.skill_writing;
        }
        if (payload.audio_question !== undefined) {
            requestBody.audio_question = payload.audio_question;
        }
        if (payload.audio_choice !== undefined) {
            requestBody.audio_choice = payload.audio_choice;
        }
        if (payload.audio_assistant_voice !== undefined) {
            requestBody.audio_assistant_voice = payload.audio_assistant_voice;
        }
        if (payload.audio_background !== undefined) {
            requestBody.audio_background = payload.audio_background;
        }

        return apiServices
            .put(`/api/correct-answers/${payload.correctAnswerId}`, requestBody, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error updateCorrectAnswer:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    hardDeleteCorrectAnswer(payload: DeleteCorrectAnswerPayload) {
        return apiServices
            .delete(`/api/correct-answers/${payload.correctAnswerId}`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error hardDeleteCorrectAnswer:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    softDeleteCorrectAnswer(payload: DeleteCorrectAnswerPayload) {
        return apiServices
            .patch(`/api/correct-answers/${payload.correctAnswerId}/soft-delete`, {}, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error softDeleteCorrectAnswer:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    hardDeleteMediaFileAndHint(payload: HardDeleteMediaFieldPayload) {
        const allowed = new Set<string>(CORRECT_ANSWER_MEDIA_FIELDS as readonly string[]);
        if (!allowed.has(String(payload.field))) {
            return Promise.resolve({
                status: "failed",
                errMessage: `invalid field: ${String(payload.field)}`,
                error: "invalid_field",
            });
        }

        return apiServices
            .patch(`/api/correct-answers/${payload.correctAnswerId}/hard-delete-field`, { field: payload.field }, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error hardDeleteMediaFileAndHint:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

}

export default correctAnswerAPI;